import { computed, Injectable, signal } from '@angular/core';
import { Observable, throwError, timer, Subject, Subscription, defer, EMPTY } from 'rxjs';
import { catchError, expand, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { ProjectApiService } from '../../../core/api/projects/project-api.service';
import { ProjectDto, StyleDto } from '../../../core/api/_dtos/projects.dto';
import { ProductDto } from '../../../core/api/_dtos/product.dto';
import { ProductsService } from '../../../shared/services/data/products.service';

export type ProjectCreatorStep = 'pickImage' | 'chooseStyle' | 'chooseProduct' | 'order';

@Injectable({
  providedIn: 'root'
})
export class ProjectCreatorService {

  // ─── Core state signals ────────────────────────────────────────
  readonly userIsWorkingOnProject = signal(false);
  readonly isGeneratingImage = signal<boolean>(false);
  readonly currentProject = signal<ProjectDto | null>(null);
  readonly productId = signal<string | null>(null);
  readonly variantId = signal<number | null>(null);
  readonly styleId = signal<string | null>(null);
  readonly quantity = signal<number>(1);
  readonly currentProduct = signal<ProductDto | null>(null);
  readonly isLoadingProduct = signal<boolean>(false);
  readonly hasImage = signal(false);
  readonly imageVersion = signal(0);
  readonly additionalData = signal<Record<string, string>>({});
  readonly isEditingPlacement = signal<boolean>(false);
  readonly isMobileEditing = signal<boolean>(false);

  // ─── Preview capture ──────────────────────────────────────────
  /** Set by ImageProductPlacement component so other components can request a snapshot. */
  capturePreviewFn: (() => Promise<Blob | null>) | null = null;

  // ─── Derived state (computed) ─────────────────────────────────
  readonly productTechnique = computed(() => this.currentProduct()?.technique ?? null);

  /**
   * Step is COMPUTED from actual state — not manually set.
   * This ensures the stepper is always correct, even after page refresh.
   */
  readonly step = computed<ProjectCreatorStep>(() => {
    const project = this.currentProject();
    if (project?.generation_status === 'completed' && this.productId()) return 'order';
    if (project?.generation_status === 'completed') return 'chooseProduct';
    if (this.isGeneratingImage()) return 'chooseStyle';
    if (this.hasImage()) return 'chooseStyle';
    return 'pickImage';
  });

  // ─── Private state ────────────────────────────────────────────
  private tempImage: File | null = null;
  private tempStyle: StyleDto | null = null;
  private loadProductSub: Subscription | null = null;
  private readonly fileInputClickSubject = new Subject<null>();
  readonly fileInputClick$ = this.fileInputClickSubject.asObservable();

  constructor(
    private projectApiService: ProjectApiService,
    private productsService: ProductsService
  ) {
  }

  // --- IMAGE ---
  setImage(image: File | null) {
    this.tempImage = image;
    this.hasImage.set(image !== null);
    this.imageVersion.update(v => v + 1);
    if (image) {
      this.userIsWorkingOnProject.set(true);
    }
    return this;
  }

  getImage() {
    return this.tempImage;
  }

  // --- STYLE ---
  setStyle(style: StyleDto | null) {
    this.tempStyle = style;
    this.styleId.set(style?.id ?? null);
    this.additionalData.set({});
    return this;
  }

  getStyle() {
    return this.tempStyle;
  }

  // --- ADDITIONAL DATA ---
  setAdditionalData(data: Record<string, string>) {
    this.additionalData.set(data);
    return this;
  }

  updateAdditionalDataField(key: string, value: string) {
    this.additionalData.update(current => ({ ...current, [key]: value }));
  }

  // --- VARIANT ID ---
  setVariantId(variantId: number | null) {
    this.variantId.set(variantId);
    return this;
  }

  // --- QUANTITY ---
  setQuantity(quantity: number) {
    this.quantity.set(quantity);
    const project = this.currentProject();
    if (project) {
      this.currentProject.set({ ...project, quantity });
    }
    return this;
  }

  getQuantity(): number {
    return this.currentProject()?.quantity ?? this.quantity();
  }

  // --- PRODUCT ID ---
  setProductId(productId: string | null) {
    this.productId.set(productId);

    if (productId && this.currentProduct()?.id !== productId) {
      this.loadProduct(productId);
    }

    return this;
  }

  // --- INITIALIZE FROM URL PARAMS ---
  initializeFromParams(params: { [key: string]: any }) {
    const variantId = params['variantId'] ? parseInt(params['variantId'], 10) : null;
    const quantity = params['quantity'] ? parseInt(params['quantity'], 10) : null;
    this.setProductId(params['productId'])
      .setVariantId(!isNaN(variantId as number) ? variantId : null)
      .setStyle(params['styleId'] ? { id: params['styleId'] } as any : null);

    if (quantity && !isNaN(quantity) && quantity >= 1) {
      this.setQuantity(quantity);
    }
  }

  // --- HELPERS ---
  canCreateImage() {
    return this.tempImage !== null
      && this.tempStyle !== null
      && this.tempStyle.id !== null; // Style must have ID (actually selected, not just placeholder)
  }

  canLeaveProject(): boolean {
    return !this.userIsWorkingOnProject();
  }

  triggerFileInputClick() {
    this.fileInputClickSubject.next(null);
  }

  // --- CREATE IMAGE ---
  public createImage(): Observable<ProjectDto> {
    if (!this.canCreateImage()) {
      return throwError(
        () => new Error('Image and style must be set before creating an image')
      );
    }

    this.userIsWorkingOnProject.set(true);
    this.isGeneratingImage.set(true);

    return this.projectApiService.createProject(null).pipe(
      switchMap(project => this.generateImageForProject(project)),
      catchError(error => {
        this.isGeneratingImage.set(false);
        return throwError(() => error);
      }),
      finalize(() => {
        this.resetTemporaryState();
      })
    );
  }

  private generateImageForProject(project: ProjectDto): Observable<ProjectDto> {
    this.currentProject.set(project);
    const additionalData = this.additionalData();

    return this.projectApiService.createImage(
      project.id,
      this.tempStyle!.id,
      this.tempImage!,
      Object.keys(additionalData).length > 0 ? additionalData : undefined
    ).pipe(
      switchMap(response => {
        this.currentProject.set(response.data);
        return this.pollForGenerationComplete(project.id);
      })
    );
  }

  private pollForGenerationComplete(projectId: string): Observable<ProjectDto> {
    const POLL_DELAY = 2000;
    const MAX_POLLS = 90; // ~3 minutes

    return defer(() => this.projectApiService.getProjectStatus(projectId)).pipe(
      expand((response, index) => {
        if (index >= MAX_POLLS) {
          return throwError(() => new Error('Generation timeout - please refresh the page'));
        }
        const status = response.data.generation_status;
        if (status !== 'pending' && status !== 'processing') return EMPTY;
        return timer(POLL_DELAY).pipe(switchMap(() => this.projectApiService.getProjectStatus(projectId)));
      }),
      map(response => response.data),
      tap(project => {
        this.currentProject.set(project);
        if (project.generation_status === 'failed') {
          throw new Error(project.generation_error || 'Image generation failed');
        }
      }),
      filter(project => project.generation_status === 'completed')
    );
  }

  /**
   * Check if generation is in progress (for UI)
   */
  isGenerationInProgress(): boolean {
    const status = this.currentProject()?.generation_status;
    return status === 'pending' || status === 'processing';
  }

  private resetTemporaryState(): void {
    this.tempImage = null;
    this.tempStyle = null;
    this.additionalData.set({});
    this.isGeneratingImage.set(false);
  }

  // --- LOAD PROJECT ---
  public loadProject(projectId: string): Observable<ProjectDto> {
    return this.projectApiService.getProjectById(projectId).pipe(
      tap(response => {
        this.currentProject.set(response.data);

        // If generation is in progress, start polling in background
        const status = response.data.generation_status;
        if (status === 'pending' || status === 'processing') {
          this.isGeneratingImage.set(true);
          // Start polling in background (don't block the initial emission)
          this.pollForGenerationComplete(projectId).pipe(
            finalize(() => this.isGeneratingImage.set(false))
          ).subscribe({
            error: (err) => console.error('Polling error:', err)
          });
        }
      }),
      map(response => response.data),
      catchError((error: any) => throwError(() => error))
    );
  }

  clear() {
    this.tempImage = null;
    this.tempStyle = null;
    this.hasImage.set(false);
    this.userIsWorkingOnProject.set(false);
    this.currentProject.set(null);
    this.productId.set(null);
    this.variantId.set(null);
    this.styleId.set(null);
    this.quantity.set(1);
    this.currentProduct.set(null);
    this.isLoadingProduct.set(false);
    this.isGeneratingImage.set(false);
    this.isEditingPlacement.set(false);
    this.isMobileEditing.set(false);
    this.loadProductSub?.unsubscribe();
  }

  resetToGenerate() {
    this.currentProject.set(null);
    this.tempImage = null;
    this.tempStyle = null;
    this.hasImage.set(false);
    this.isEditingPlacement.set(false);
    this.isMobileEditing.set(false);
  }

  /**
   * Build current query params from service state (for URL sync).
   * Returns only non-null params.
   */
  buildQueryParams(): Record<string, string> {
    const params: Record<string, string> = {};
    const project = this.currentProject();

    if (project?.id) {
      params['projectId'] = project.id;
    } else if (this.styleId()) {
      params['styleId'] = this.styleId()!;
    }

    if (this.variantId() !== null && !isNaN(this.variantId()!)) {
      params['variantId'] = this.variantId()!.toString();
    }

    if (this.productId()) {
      params['productId'] = this.productId()!;
    }

    return params;
  }

  // --- LOAD PRODUCT ---
  private loadProduct(productId: string): void {
    // Cancel any in-flight product load so the latest request always wins
    this.loadProductSub?.unsubscribe();

    this.isLoadingProduct.set(true);

    this.loadProductSub = this.productsService.getProduct(productId).subscribe({
      next: (response) => {
        this.currentProduct.set(response.data);
        this.isLoadingProduct.set(false);
      },
      error: (err) => {
        console.error('Error loading product:', err);
        this.isLoadingProduct.set(false);
      }
    });
  }
}
